import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Loader2, Sparkles, X } from "lucide-react";
import { defaultSections, defaultSkill, skillSchema } from "@reactive-resume/schema";
import {
  Badge,
  BadgeInput,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Slider,
  Tooltip,
  TooltipProvider,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { toast } from "@/client/hooks/use-toast";
import { aiService } from "@/client/services/aiService";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";

const formSchema = skillSchema;

type FormValues = z.infer<typeof formSchema>;

export const SkillsDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultSkill,
    resolver: zodResolver(formSchema),
  });
  const nameValue = useWatch({ control: form.control, name: "name" });
  const keywords = useWatch({ control: form.control, name: "keywords" });
  const jobDescription = useResumeStore((state) => state.resume.jobDescription);
  const skillsSection = useResumeStore(
    (state) => state.resume.data.sections.skills ?? defaultSections.skills,
  );
  const normalizedKeywords = useMemo(
    () => (keywords ?? []).map((item) => item.trim()).filter((item) => item.length > 0),
    [keywords],
  );
  const keywordsKey = useMemo(() => normalizedKeywords.join("|").toLowerCase(), [normalizedKeywords]);
  const existingSkills = useMemo(
    () =>
      skillsSection.items.map((skill) => ({
        name: skill.name,
        keywords: skill.keywords ?? [],
      })),
    [skillsSection.items],
  );
  const existingSkillsKey = useMemo(
    () => existingSkills.map((skill) => skill.name).join("|").toLowerCase(),
    [existingSkills],
  );
  const [suggestion, setSuggestion] = useState<{
    name: string;
    confidence: number;
    alternatives?: string[];
  } | null>(null);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const lastKeywordsKeyRef = useRef<string>("");
  const lastRequestKeyRef = useRef<string>("");

  const showSuggestion = !nameValue?.trim() && suggestion && !isDismissed;
  const showLoading =
    !nameValue?.trim() && isSuggesting && !suggestion && !isDismissed && normalizedKeywords.length >= 2;

  const confidenceClasses = (confidence: number) => {
    if (confidence >= 80) {
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700/60 dark:bg-emerald-900/20 dark:text-emerald-300";
    }
    if (confidence >= 60) {
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-300";
    }
    return "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700/60 dark:bg-gray-800/40 dark:text-gray-300";
  };

  useEffect(() => {
    if (!keywordsKey) {
      setSuggestion(null);
      setIsDismissed(false);
      lastKeywordsKeyRef.current = "";
      lastRequestKeyRef.current = "";
      return;
    }
    if (keywordsKey !== lastKeywordsKeyRef.current) {
      setIsDismissed(false);
      lastKeywordsKeyRef.current = keywordsKey;
    }
  }, [keywordsKey]);

  useEffect(() => {
    const requestKey = `${keywordsKey}|${existingSkillsKey}|${jobDescription?.trim() ?? ""}`;

    if (nameValue?.trim()) {
      setSuggestion(null);
      setIsDismissed(false);
      return;
    }

    if (normalizedKeywords.length < 2 || isDismissed) {
      setSuggestion(null);
      return;
    }

    if (requestKey === lastRequestKeyRef.current || isSuggesting) return;

    const timer = setTimeout(async () => {
      setIsSuggesting(true);
      setSuggestion(null);
      lastRequestKeyRef.current = requestKey;
      try {
        const result = await aiService.suggestSkillCategory(
          normalizedKeywords,
          existingSkills,
          jobDescription?.trim() || undefined,
        );

        if (result.categoryName) {
          setSuggestion({
            name: result.categoryName,
            confidence: result.confidence ?? 0,
            alternatives: result.alternatives ?? [],
          });
        }
      } catch (error) {
        lastRequestKeyRef.current = "";
        toast({
          variant: "error",
          title: t`Suggestion Failed`,
          description: (error as Error)?.message || t`Unable to suggest a category name right now.`,
        });
      } finally {
        setIsSuggesting(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    nameValue,
    normalizedKeywords,
    keywordsKey,
    existingSkillsKey,
    existingSkills,
    jobDescription,
    isDismissed,
    isSuggesting,
  ]);

  const handleApplySuggestion = () => {
    if (!suggestion) return;
    form.setValue("name", suggestion.name, { shouldDirty: true, shouldValidate: true });
    setSuggestion(null);
  };

  return (
    <SectionDialog<FormValues> id="skills" form={form} defaultValues={defaultSkill}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="name"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Name`}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              {(showSuggestion || showLoading) && (
                <div className="mt-2 flex items-center gap-2">
                  {showLoading && (
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs text-blue-700 dark:border-blue-700/60 dark:bg-blue-900/20 dark:text-blue-300">
                      <Loader2 size={12} className="animate-spin" />
                      <span>{t`Finding a category name...`}</span>
                    </div>
                  )}
                  {showSuggestion && suggestion && (
                    <TooltipProvider>
                      <Tooltip
                        content={
                          suggestion.alternatives && suggestion.alternatives.length > 0
                            ? `${t`Alternatives`}: ${suggestion.alternatives.join(", ")}`
                            : t`No alternatives available`
                        }
                      >
                        <button
                          type="button"
                          onClick={handleApplySuggestion}
                          className={cn(
                            "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-all hover:shadow-sm",
                            confidenceClasses(suggestion.confidence),
                          )}
                          aria-label={t`Apply suggested category name`}
                        >
                          <Sparkles size={12} />
                          <span>
                            {t`Suggested`}: {suggestion.name}
                          </span>
                          <span className="opacity-70">{suggestion.confidence}%</span>
                        </button>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {showSuggestion && (
                    <button
                      type="button"
                      onClick={() => setIsDismissed(true)}
                      className="inline-flex items-center justify-center rounded-full border border-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                      aria-label={t`Dismiss suggestion`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Description`}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="level"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Level`}</FormLabel>
              <FormControl className="py-2">
                <div className="flex items-center gap-x-4">
                  <Slider
                    {...field}
                    min={0}
                    max={5}
                    value={[field.value]}
                    orientation="horizontal"
                    onValueChange={(value) => field.onChange(value[0])}
                  />

                  {field.value === 0 ? (
                    <span className="text-base font-bold">{t`Hidden`}</span>
                  ) : (
                    <span className="text-base font-bold">{field.value}</span>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="keywords"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-3 sm:col-span-2">
              <FormItem>
                <FormLabel>{t`Keywords`}</FormLabel>
                <FormControl>
                  <BadgeInput {...field} />
                </FormControl>
                <FormDescription>
                  {t`You can add multiple keywords by separating them with a comma or pressing enter.`}
                </FormDescription>
                <FormMessage />
              </FormItem>

              <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                <AnimatePresence>
                  {field.value.map((item, index) => (
                    <motion.div
                      layout
                      key={item}
                      initial={{ opacity: 0, y: -50 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: index * 0.1 } }}
                      exit={{ opacity: 0, x: -50 }}
                    >
                      <Badge
                        className="cursor-pointer"
                        onClick={() => {
                          field.onChange(field.value.filter((v) => item !== v));
                        }}
                      >
                        <span className="mr-1">{item}</span>
                        <X size={12} />
                      </Badge>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        />
      </div>
    </SectionDialog>
  );
};
